/** @jsxImportSource @emotion/react */
import React, { useMemo } from "react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { Text } from "../../../components/general/Text";
import { getTokenIcon } from "../../../utils/tokenIconUtils";
import { SuccessTick } from "../../../components/SuccessTick/SuccessTick";
import { Button, ButtonBorderRadius } from "../../../components/general/Button/Button";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { css } from "@emotion/react";
import { getShareTweetLink } from "../../../utils/shareTradeUtils";
import { getTokenOutSummary } from "../../../utils/quoteUtils";
import { Amount } from "../../../types/amount";
import { Token } from "../../../store/global/TokenEnum";
import { calculateUsdTradeSurplus } from "../../../utils/orderUtils";
import { ReactComponent as Highlighted } from "../../../img/Highlighted.svg";
import { AMOUNT_TO_SHOW_TRADE_SURPLUS } from "../../../config/consts";

export const YouReceiveSection = () => {
  const { takerTokens, makerTokens, theme, quote, order, wrapQuote } = GlobalStore.useState(s => {
    return {
      takerTokens: s.takerTokens,
      makerTokens: s.makerTokens,
      theme: s.theme,
      quote: s.quote,
      order: s.order,
      wrapQuote: s.wrapQuote,
    };
  });
  const tokenList = useTokenListByChain();
  const shareTweetLink = getShareTweetLink(
    takerTokens.map(token => token.token),
    makerTokens.map(token => token.token)
  );
  const tradeSurplus = calculateUsdTradeSurplus(order, quote);

  const totalUsdAmount = useMemo(() => {
    if (quote && order) {
      const usdAmount = Object.entries(quote.makerTokens).reduce((total, [token, value]) => {
        const quotePrice = quote?.makerTokens[token as Token]?.priceUsd;
        const amountReceived = order.amountsReceived[value.contractAddress];
        const usdValue = quotePrice.mul(amountReceived);
        return total.add(usdValue);
      }, Amount.ZERO);
      return usdAmount.format(2);
    } else if (wrapQuote) {
      return takerTokens[0].amount.format(2);
    }
  }, [quote, order, wrapQuote, takerTokens]);

  if (quote === undefined && !wrapQuote) {
    return null;
  }

  const getMakerTokenAmount = (makerToken: Token): string => {
    if (quote) {
      return order
        ? order.amountsReceived[quote.makerTokens[makerToken].contractAddress].toDisplay(
            makerToken,
            tokenList
          )
        : getTokenOutSummary(quote, makerToken).amount.toDisplay(makerToken, tokenList);
    }
    return wrapQuote?.amount?.toDisplay(makerToken, tokenList) ?? "";
  };

  return (
    <ReceiveSectionContainer>
      <ReceiveSectionContent
        variants={visibilityVariants}
        initial="enter"
        animate="animate"
        custom={tradeSurplus}
      >
        <TradeCompleteContainer>
          <SuccessTick />
          <Text textStyle="Title" size="Large" color={theme.surface.main.primary.default}>
            Trade complete
          </Text>
        </TradeCompleteContainer>
        <ReceivedContainer>
          {tradeSurplus.value.gt(0.0) && (
            <Text
              textStyle={"Title"}
              size={"Small"}
              color={theme.surface.main.primary.default}
              textCss={css`
                display: flex;
              `}
            >
              <HighlightedContainer>
                You received
                <HighlightedText>
                  {" "}
                  <HighlightedSvg preserveAspectRatio="none" />${tradeSurplus.format(2)} extra
                </HighlightedText>{" "}
                on this trade!
              </HighlightedContainer>{" "}
            </Text>
          )}
          {tradeSurplus.value.lt(0.01) && (
            <Text textStyle="Title" size="Small" color={theme.surface.main.primary.default}>
              You received
            </Text>
          )}
          <Text textStyle="Title" size="XXL" color={theme.surface.main.primary.default}>
            {makerTokens.map(makerToken => {
              const amount = getMakerTokenAmount(makerToken.token);
              return (
                <ReceivedTokenRow key={makerToken.token}>
                  <TokenIcon src={getTokenIcon(makerToken.token, tokenList)} alt="Receive token" />{" "}
                  {amount} {makerToken.token}
                </ReceivedTokenRow>
              );
            })}
          </Text>
          <Text textStyle="Paragraph" size="XXL" color={theme.surface.main.primary.default}>
            ${totalUsdAmount}
          </Text>
        </ReceivedContainer>
        <Button
          buttonType={"secondary"}
          onClick={() => window.open(shareTweetLink, "_blank")}
          text={"Share my trade"}
          textSize={"Medium"}
          padding={"12px 14px"}
          buttonCss={css`
            border-radius: ${ButtonBorderRadius.Mid};
          `}
        />
      </ReceiveSectionContent>
    </ReceiveSectionContainer>
  );
};

export const visibilityVariants = {
  enter: (surplus: Amount) => {
    if (surplus.value.gt(AMOUNT_TO_SHOW_TRADE_SURPLUS)) {
      return {
        opacity: 0,
      };
    }
    return {};
  },
  animate: (surplus: Amount) => {
    if (surplus.value.gt(AMOUNT_TO_SHOW_TRADE_SURPLUS)) {
      return {
        opacity: 1,
        transition: {
          opacity: { type: "easeIn", duration: 0.8 },
          delay: 0.5,
        },
      };
    } else {
      return {};
    }
  },
};

const ReceiveSectionContainer = styled(motion.div)`
  display: flex;
  padding: 26px 0px 12px 0px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 36px 36px 0 0;
  background: ${props => props.theme["Dark mode"]["1700"]};
  position: relative;
`;

const ReceiveSectionContent = styled(motion.div)`
  display: flex;
  padding-top: 12px;
  flex-direction: column;
  align-items: center;
  gap: 25px;
  align-self: stretch;
`;

const TradeCompleteContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const ReceivedContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  align-self: stretch;
`;

const HighlightedContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  position: relative;
`;

const HighlightedSvg = styled(Highlighted)`
  position: absolute;
`;

const HighlightedText = styled.span`
  font-weight: 600;
  display: flex;
  align-items: center;
  position: relative;
`;

const TokenIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const ReceivedTokenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
